/**
 * Session map loading policy (ENT-4777), kept pure so it is unit-tested in
 * `sessionExplorerMaps.test.ts`:
 *  - which ids to ask the batch `map/` endpoint for (selected + next 2),
 *  - fetching a presigned map with one fresh-URL retry on expiry / network
 *    failure.
 */
import type { SessionMap } from './sessionExplorerSchema';

/** How many sessions after the selected one to sign and prefetch. */
export const MAP_PREFETCH_AHEAD = 2;

/**
 * Ids for one batch `map/` call: the selected session first, then the next
 * `ahead` sessions in list order, skipping ids already known to have no map.
 * A selected id that is not in the (filtered) list is still requested alone.
 */
export function mapRequestIds(
  listIds: readonly string[],
  selectedId: string,
  unavailable: ReadonlySet<string> = new Set(),
  ahead = MAP_PREFETCH_AHEAD
): string[] {
  const out = [selectedId];
  const start = listIds.indexOf(selectedId);
  if (start < 0) return out;
  for (let i = start + 1; i < listIds.length && out.length <= ahead; i++) {
    const id = listIds[i];
    if (!unavailable.has(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

/** True for failures a freshly signed URL can fix: S3 403 (signature
 * expired) or a network-level fetch failure. */
export function isUrlFailure(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  return (
    !!err &&
    typeof err === 'object' &&
    'code' in err &&
    (err as { code: unknown }).code === 'expired'
  );
}

/**
 * Fetch a map document from `url`; if that fails because the URL expired or
 * the network dropped, ask for a fresh URL once and try again. Any other
 * failure, or a second failure, is thrown to the caller (inline error card).
 */
export async function fetchMapWithRetry(
  url: string,
  freshUrl: () => Promise<string>,
  fetchDoc: (url: string) => Promise<SessionMap>
): Promise<SessionMap> {
  try {
    return await fetchDoc(url);
  } catch (err) {
    if (!isUrlFailure(err)) throw err;
    return fetchDoc(await freshUrl());
  }
}
